import {
  HR_CATEGORY_ID,
  INCIDENT_CATEGORY_ID,
  ITEM_ID_SEIZURE,
  ITEM_ID_SHORT_SEIZURE,
  SAFEGUARDING_ITEM_ID
} from '@lmc/common/types'
import type { LogCategory, Resident } from '@lmc/ts-common'
import useFeatureFlags from 'hooks/useFeatureFlags'
import _ from 'lodash'
import { type FC, Suspense, useEffect, useState } from 'react'
import { useQueryClient } from 'react-query'
import { connect } from 'react-redux'
import styled from 'styled-components'
import { postJSON } from '../../../common/services/api'
import { showToast } from '../../../common/utils'
import { ScrollLock } from '../../../LEGACY/elemental'
import Operations from '../../../operations'
import Selectors from '../../../selectors'
import Field from '../../fields'
import { Step, StepList } from '../../generic'
import Modal from '..'
import {
  ModalCategoryItemSelector,
  ModalCategorySelector,
  ModalResidentSelector,
  ModalTitleSelector
} from '../components'
import {
  BODY_MAPS_CATEGORY,
  isCustomEntryVisible
} from '../customEntryVisibility'
import LogsGoalsSelect from './LogsGoalsSelect'
import LogsSeizureDeEscalation from './LogsSeizureDeEscalation'

enum AddLogStep {
  SU = 0,
  CATEGORY = 1,
  ITEM = 2,
  LOG = 3
}

interface CustomEntry {
  id: string
  category: string
  custom: true
  isHidden?: boolean
}

interface AddLogModalProps {
  categories: LogCategory[]
  categoryItems: any[]
  /** CM-4043: the synthesised per-category Custom entries, keyed on category id. */
  customEntries: CustomEntry[]
  /** False until the withCustom fetch has completed at least once. */
  customEntriesLoaded: boolean
  loadCustomEntries: () => void
  close: () => void
  createItem: (args: {
    url: string
    json: any
    onComplete: () => void
    onError: () => void
  }) => void
  fetchLogs: () => void
  fields: any // Add the appropriate type
  hasPro: boolean
  isOpen: boolean
  residents: Resident[]
  summary: {
    icon: {
      color: string
      url: string
    }
    text: string
  }
  setFormField: (args: { key: string; value: any }) => void
  taskCompletion?: boolean
  testId?: string
}

export const AddLogModal: FC<AddLogModalProps> = ({
  categories,
  categoryItems,
  customEntries,
  customEntriesLoaded,
  loadCustomEntries,
  close,
  hasPro,
  isOpen,
  residents,
  summary,
  taskCompletion,
  testId,
  ...formProps
}) => {
  const [isLoading, setIsLoading] = useState(false)
  const queryClient = useQueryClient()

  // CM-4043: fetched on open rather than in the app-wide bootstrap — only this
  // modal needs custom entries, and an admin can change the setting between
  // opens, so a stale bootstrap copy would show a Custom option that is off.
  useEffect(() => {
    if (isOpen) loadCustomEntries()
  }, [isOpen, loadCustomEntries])

  const flags = useFeatureFlags({
    flagNames: ['clinical_safety_seizures', 'custom_log_toggle'] as const
  })?.data

  const checkTitleValid = () => {
    if (!formProps.fields.title || !formProps.fields.description) return false
    return true
  }

  const filterResidentsByStatus = (residents: any[]) =>
    _.filter(residents, (resident) =>
      PERMITTED_RESIDENT_STATUSES.includes(resident.status)
    )

  const getHeaderProps = () => {
    const selectedResident = _.find(residents, {
      id: formProps.fields.resident
    })
    if (!selectedResident) return null

    return {
      isMainHeader: true,
      profilePicture: selectedResident.picture,
      title: `${selectedResident.name.first} ${selectedResident.name.last}`
    }
  }

  const handleTaskCompletionWithLog = async (
    taskId: string,
    taskStatusPath: string,
    taskOnSuccess: (() => void) | undefined,
    logData: any
  ) => {
    try {
      // Step 1: Complete the task, which creates the completion log. The note
      // must stay empty: the backend appends the step-2 description onto that
      // log, so sending the text here too duplicates it (CM-4241).
      await postJSON({
        method: 'POST',
        url: `list/tasks/${taskId}/${taskStatusPath}`,
        json: {
          note: ''
        }
      })

      // Step 2: Create the log with the task ID
      // Backend will find the existing log and merge the data
      formProps.createItem({
        url: 'list/logs',
        json: {
          ...logData,
          task: taskId // Include task ID so backend merges the logs
        },
        onComplete: () => {
          formProps.fetchLogs()
          queryClient.invalidateQueries(['incident-list'])
          showToast({ title: 'Completed To-Do and Added Log' })

          // Call the task's onSuccess callback to refresh task lists
          if (taskOnSuccess) {
            taskOnSuccess()
          }

          setIsLoading(false)
        },
        onError: () => {
          showToast({
            title: 'Task completed but failed to add log details',
            type: 'error'
          })
          setIsLoading(false)
        }
      })
    } catch (error) {
      showToast({
        title: 'Failed to complete To-Do',
        type: 'error'
      })
      setIsLoading(false)
    }
  }

  const handleSubmit = () => {
    setIsLoading(true)
    // Extract task completion data if present
    const {
      _taskId: taskId,
      _taskStatusPath: taskStatusPath,
      _taskOnSuccess: taskOnSuccess,
      _taskComment,
      ...logData
    } = formProps.fields

    if (taskId && taskStatusPath) {
      // If this is from a task completion, complete the task first, then create the log
      handleTaskCompletionWithLog(
        taskId,
        taskStatusPath,
        taskOnSuccess,
        logData
      )
    } else {
      // Normal log creation flow (not from task completion)
      formProps.createItem({
        url: 'list/logs',
        json: logData,
        onComplete: () => {
          formProps.fetchLogs()
          queryClient.invalidateQueries(['incident-list'])
          showToast({ title: 'Added a Log' })
          setIsLoading(false)
        },
        onError: () => {
          setIsLoading(false)
        }
      })
    }
  }

  const renderDateTimePicker = () => (
    <TimePickerContainer
      data-testid={
        testId ? `${testId}-time-picker` : 'add-log-modal-time-picker'
      }
    >
      <FormField
        data-testid={
          testId ? `${testId}-time-field` : 'add-log-modal-time-field'
        }
      >
        <Label
          data-testid={
            testId ? `${testId}-time-label` : 'add-log-modal-time-label'
          }
        >
          {TIME}
        </Label>
        <DatePickerContainer
          data-testid={
            testId
              ? `${testId}-date-picker-container`
              : 'add-log-modal-date-picker-container'
          }
        >
          <Suspense fallback={<div>Loading...</div>}>
            <Field
              formId={MODAL_ID}
              keys="timeLogged"
              layout="column"
              type="datetime"
              values={{
                ...formProps.fields,
                timeLogged: formProps.fields.timeLogged || new Date()
              }}
              data-testid={
                testId
                  ? `${testId}-time-logged-input`
                  : 'add-log-modal-time-logged-input'
              }
            />
          </Suspense>
        </DatePickerContainer>
      </FormField>
    </TimePickerContainer>
  )

  const renderFlagForHandover = () => {
    return hasPro ? (
      <HandoverContainer
        data-testid={
          testId
            ? `${testId}-handover-container`
            : 'add-log-modal-handover-container'
        }
      >
        <Suspense fallback={<div>Loading...</div>}>
          <Field
            formId={MODAL_ID}
            leftIcon="flag"
            keys="isNextHandover"
            label="Flag for handover?"
            type="singleCheckbox"
            values={formProps.fields}
            data-testid={
              testId
                ? `${testId}-handover-checkbox`
                : 'add-log-modal-handover-checkbox'
            }
          />
        </Suspense>
      </HandoverContainer>
    ) : null
  }

  if (
    !isOpen ||
    !categories ||
    !categoryItems ||
    !residents ||
    !summary ||
    !formProps.fields
  ) {
    return null
  }

  const {
    icon: { color, url },
    text
  } = summary

  // CARE-693: filter out hidden categories and items
  const visibleCategoryItems = _.filter(categoryItems, (d) => {
    if (d.isHidden) {
      return false
    }
    if (
      d.id === ITEM_ID_SHORT_SEIZURE &&
      flags?.clinical_safety_seizures.enabled
    ) {
      return false
    }
    return true
  })

  // CM-4043: the Custom log a site has switched off, per category. An absent
  // entry counts as hidden — the only way it goes missing is the ?withCustom
  // request failing, and silently offering an unfiltered Custom log is the
  // exact failure this ticket removes.
  // CM-4043 ships behind custom_log_toggle. With the flag off the Custom option
  // is always offered and categories hide on the pre-existing items-only rule
  // (CARE-693), so the Office behaves exactly as it did. Removed with the flag —
  // see CM-4228.
  const customLogToggleEnabled = Boolean(flags?.custom_log_toggle.enabled)

  // The !customEntriesLoaded arm covers the first open, before the withCustom
  // fetch lands — an empty store there means "not loaded", not "all hidden".
  const isCustomVisibleFor = (categoryId: string) =>
    !customLogToggleEnabled ||
    !customEntriesLoaded ||
    isCustomEntryVisible(customEntries, categoryId)

  // hide category only when every log in it is off, INCLUDING the Custom one
  // (AC3.1). A category whose only remaining log is Custom must stay (AC2.2).
  const visibleCategories = _.filter(categories, ({ id, itemId }) => {
    if (itemId === BODY_MAPS_CATEGORY) return false
    const anyVisibleItem = Boolean(
      _.find(visibleCategoryItems, { category: id })
    )
    if (!customLogToggleEnabled) return anyVisibleItem
    return anyVisibleItem || isCustomVisibleFor(id)
  })

  const { category } = formProps.fields
  const chosenItems = category && _.filter(visibleCategoryItems, { category })
  const chosenItem =
    formProps.fields &&
    formProps.fields.item &&
    _.find(visibleCategoryItems, { id: formProps.fields.item })
  const chosenResidents = filterResidentsByStatus(residents)
  const selectedResident = _.find(residents, {
    id: formProps.fields.resident
  })

  const onChangeSeizureType = (deEscalated: boolean) => {
    const itemId = deEscalated ? ITEM_ID_SHORT_SEIZURE : ITEM_ID_SEIZURE
    const categoryId = deEscalated ? HR_CATEGORY_ID : INCIDENT_CATEGORY_ID
    const title = categoryItems?.find((item) => item?.id === itemId)?.name

    formProps.setFormField({
      key: 'item',
      value: itemId
    })
    formProps.setFormField({
      key: 'category',
      value: categoryId
    })
    formProps.setFormField({
      key: 'title',
      value: title
    })
  }

  const isSeizure =
    formProps.fields.item === ITEM_ID_SEIZURE ||
    formProps.fields.item === ITEM_ID_SHORT_SEIZURE

  const showSeizureSwitch = flags?.clinical_safety_seizures.enabled && isSeizure

  const isSafeguarding = chosenItem?._id === SAFEGUARDING_ITEM_ID

  // If we open the modal from the task page, we have the information already collected.
  // Start from the final step.
  const hasRequiredData =
    formProps.fields.resident &&
    formProps.fields.category &&
    formProps.fields.item
  const initialStep =
    taskCompletion && hasRequiredData ? AddLogStep.LOG : AddLogStep.SU

  return (
    <div
      data-cy="create-log-item-container"
      data-testid={testId || 'add-log-modal'}
    >
      <StepList
        disableNavigation={taskCompletion}
        backIconColor={color}
        backIcon={url}
        backText={text}
        closeLabel="Cancel"
        headerProps={getHeaderProps()}
        onClose={close}
        initialStep={initialStep}
        data-testid={testId ? `${testId}-step-list` : 'add-log-modal-step-list'}
      >
        <Step
          component={ModalResidentSelector}
          data={{ residents: chosenResidents }}
          testId={
            testId
              ? `${testId}-resident-selector`
              : 'add-log-modal-resident-selector'
          }
          {...formProps}
        />
        <Step
          component={ModalCategorySelector}
          allCategories
          data={{ categories: visibleCategories }}
          testId={
            testId
              ? `${testId}-category-selector`
              : 'add-log-modal-category-selector'
          }
          {...formProps}
        />
        <Step
          component={ModalCategoryItemSelector}
          data={{ items: chosenItems, categories: visibleCategories }}
          showCustom={Boolean(category) && isCustomVisibleFor(category)}
          testId={
            testId
              ? `${testId}-category-item-selector`
              : 'add-log-modal-category-item-selector'
          }
          {...formProps}
        />
        <Step
          component={ModalTitleSelector}
          isValid={checkTitleValid}
          onSubmit={handleSubmit}
          formId={MODAL_ID}
          submitButtonText="Add Log"
          buttonGlyph="plus"
          item={chosenItem}
          validationText="Title and Description is required"
          stepHeader={renderDateTimePicker()}
          stepFooter={
            !isSafeguarding ? (
              <>
                <LogsGoalsSelect
                  residentID={selectedResident?.id}
                  onChange={(value) =>
                    formProps.setFormField({
                      key: 'goals',
                      value
                    })
                  }
                  data-testid={
                    testId
                      ? `${testId}-goals-select`
                      : 'add-log-modal-goals-select'
                  }
                />
                {showSeizureSwitch && (
                  <LogsSeizureDeEscalation
                    deEscalated={
                      formProps.fields.item === ITEM_ID_SHORT_SEIZURE
                    }
                    onChange={onChangeSeizureType}
                    data-testid={
                      testId
                        ? `${testId}-seizure-de-escalation`
                        : 'add-log-modal-seizure-de-escalation'
                    }
                  />
                )}
                {renderFlagForHandover()}
              </>
            ) : undefined
          }
          selectedResident={selectedResident}
          isLoading={isLoading}
          testId={
            testId ? `${testId}-title-selector` : 'add-log-modal-title-selector'
          }
          {...formProps}
        />
      </StepList>
      <ScrollLock />
    </div>
  )
}

const TIME = 'Time'
const PERMITTED_RESIDENT_STATUSES = [
  'active',
  'hospitalised',
  'released',
  'dummy'
]
const MODAL_ID = 'addLog'

/**
 * CM-4043. Its own store key, deliberately not `log-category-items`: that slice
 * is shared with incident filters, monitoring reports, goals timelines and
 * consent screens, and the synthesised custom entry carries a CATEGORY id.
 */
export const CUSTOM_ENTRIES_LIST_ID = 'log-category-custom-entries'

const mapStateToProps = (state: any, props: any) => ({
  categories: state.data['log-categories'],
  categoryItems: state.data['log-category-items'],
  // CM-4043: kept in its own store key rather than folded into
  // `log-category-items`. That slice is read directly by incident filters,
  // monitoring reports, goals timelines and consent screens, and the custom
  // entry's `id` is a CATEGORY id — mixing it in would put a phantom "Custom
  // entry" in all of those lists and risk find({ id }) collisions.
  customEntries: _.filter(state.data[CUSTOM_ENTRIES_LIST_ID], { custom: true }),
  customEntriesLoaded: state.data[CUSTOM_ENTRIES_LIST_ID] !== undefined,
  hasPro: _.get(state, 'global.home.pro'),
  residents: state.data.residents,
  // @ts-ignore
  summary: Selectors.getTodoSummary(state, props)
})

const mapDispatchToProps = (dispatch: any) => ({
  fetchLogs: () =>
    dispatch(Operations.fetchLogs({ search: '', timeLogged: {} })),
  loadCustomEntries: () =>
    dispatch(
      Operations.loadList(
        CUSTOM_ENTRIES_LIST_ID,
        null,
        true,
        '/api/list/log-category-items?withCustom=true'
      )
    )
})

const wrappedModal = connect(mapStateToProps, mapDispatchToProps)(AddLogModal)

export default Modal(wrappedModal, MODAL_ID)

const DatePickerContainer = styled.div`
  margin-top: 4px;
`

const FormField = styled.div`
  max-width: 400px;
  padding-bottom: 15px;
`

const HandoverContainer = styled.div`
  margin: auto;
  max-width: 400px;
`

const Label = styled.label`
  font-weight: 600;
  text-align: left;
`

const TimePickerContainer = styled.div`
  margin: auto;
  max-width: 400px;
`
